import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { apiFetch } from '#/api/client'
import { questionnaireKeys, questionnairePath } from '#/api/questionnaires'

// A scoping tag a workspace's questionnaire response is in scope for. `group`
// is the tag's axis (e.g. 'questionnaire-sector'). Mirrors the BFF
// `ApplicabilityTag`.
type ApplicabilityTag = {
  code: string
  label: string
  group: string
}

// The workspace's current selection. `configured` is false when nothing has
// been selected — the workspace then sees every question (backend default).
export type ApplicabilityConfig = {
  configured: boolean
  selected: Array<ApplicabilityTag>
}

// A selectable tag within a scoping group (catalog entry). The group axis lives
// on the parent `ApplicabilityOptionGroup`, not here.
type ApplicabilityOption = {
  code: string
  label: string
}

// One scoping axis and the tags selectable within it. Mirrors the BFF
// `ApplicabilityOptionGroup`.
export type ApplicabilityOptionGroup = {
  group: string
  tags: Array<ApplicabilityOption>
}

type ApplicabilityOptionListResponse = {
  data: Array<ApplicabilityOptionGroup>
}

export const applicabilityKeys = {
  config: (a: string, w: string, q: string) =>
    [...questionnaireKeys.all(a, w), q, 'applicability', 'config'] as const,
  options: (a: string, w: string, q: string) =>
    [...questionnaireKeys.all(a, w), q, 'applicability', 'options'] as const,
}

function applicabilityPath(
  accountUuid: string,
  workspaceUuid: string,
  questionnaireUuid: string,
) {
  return (
    questionnairePath(accountUuid, workspaceUuid, questionnaireUuid) +
    '/applicability'
  )
}

export const applicabilityConfigQueryOptions = (
  accountUuid: string,
  workspaceUuid: string,
  questionnaireUuid: string,
) =>
  queryOptions({
    queryKey: applicabilityKeys.config(
      accountUuid,
      workspaceUuid,
      questionnaireUuid,
    ),
    queryFn: ({ signal }) =>
      apiFetch<ApplicabilityConfig>({
        path: applicabilityPath(accountUuid, workspaceUuid, questionnaireUuid),
        signal,
      }),
  })

export const applicabilityOptionsQueryOptions = (
  accountUuid: string,
  workspaceUuid: string,
  questionnaireUuid: string,
) =>
  queryOptions({
    queryKey: applicabilityKeys.options(
      accountUuid,
      workspaceUuid,
      questionnaireUuid,
    ),
    queryFn: async ({ signal }) => {
      const res = await apiFetch<ApplicabilityOptionListResponse>({
        path:
          applicabilityPath(accountUuid, workspaceUuid, questionnaireUuid) +
          '/options',
        signal,
      })
      return res.data
    },
  })

export function useSetApplicability(
  accountUuid: string,
  workspaceUuid: string,
  questionnaireUuid: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tagCodes: ReadonlyArray<string>) =>
      apiFetch<ApplicabilityConfig>({
        method: 'PUT',
        path: applicabilityPath(accountUuid, workspaceUuid, questionnaireUuid),
        json: { tag_codes: [...tagCodes] },
      }),
    // The PUT returns the authoritative selection, so seed the config cache from
    // it (BFF reads can lag a write — don't invalidate that key). Then invalidate
    // the dashboard questionnaire projection: reconfiguring changes which
    // questions/stats apply, so the overview must refetch. This key does not
    // prefix-match the config key, so the seeded selection is preserved.
    onSuccess: async (data) => {
      queryClient.setQueryData(
        applicabilityKeys.config(accountUuid, workspaceUuid, questionnaireUuid),
        data,
      )
      await queryClient.invalidateQueries({
        queryKey: questionnaireKeys.dashboard(
          accountUuid,
          workspaceUuid,
          questionnaireUuid,
        ),
      })
    },
    // A failed write may have partially landed (or not) — resync the selection
    // from the server so the form can't drift from the truth.
    onError: async () => {
      await queryClient.invalidateQueries({
        queryKey: applicabilityKeys.config(
          accountUuid,
          workspaceUuid,
          questionnaireUuid,
        ),
      })
    },
  })
}
